import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import TopBar from "../components/layout/TopBar";
import { partners } from "../data/demoData";

type HistoryPeriod = "today" | "week" | "month" | "3months";

type HistoryRow = {
  toy: string;
  qty: number;
  amount: string;
  time: string;
  timestamp: number;
  status: string;
  operationType: string;
};

const ALLOWED_AMOUNTS = [290, 490, 790, 990, 1190, 1490, 1990, 2490];

const HISTORY_PRODUCT_POOL = [
  "Позиция #1",
  "Позиция #2",
  "Позиция #3",
  "Позиция #4",
  "Позиция #5",
  "Позиция #6",
  "Позиция #7",
  "Позиция #8",
  "Позиция #9",
  "Позиция #10",
  "Позиция #11",
  "Позиция #12",
  "Позиция #13",
  "Позиция #14",
  "Позиция #15",
  "Позиция #16",
];

function pad(value: number) {
  return String(value).padStart(2, "0");
}

function formatDateTime(date: Date) {
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function formatRub(value: number) {
  return `${value.toLocaleString("ru-RU")} ₽`;
}

function getYesterdayBaseDate() {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  yesterday.setHours(21, 45, 0, 0);
  return yesterday;
}

function getHistoryDate(index: number, period: HistoryPeriod) {
  const base = getYesterdayBaseDate();
  const result = new Date(base);

  let maxDaysBack = 0;

  if (period === "today") {
    maxDaysBack = 0;
  } else if (period === "week") {
    maxDaysBack = 6;
  } else if (period === "month") {
    maxDaysBack = 29;
  } else {
    maxDaysBack = 89;
  }

  const dayOffset = maxDaysBack === 0 ? 0 : index % (maxDaysBack + 1);

  result.setDate(base.getDate() - dayOffset);

  const hour = Math.max(8, 21 - (index % 12));
  const minutes = (index * 11) % 60;
  const seconds = (index * 9) % 60;

  result.setHours(hour, minutes, seconds, 0);

  return result;
}

const pageWrap: React.CSSProperties = {
  minHeight: "100vh",
};

const container: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "24px",
};

const sectionCard: React.CSSProperties = {
  background: "var(--surface)",
  borderRadius: "28px",
  padding: "28px",
  border: "1px solid var(--line)",
  boxShadow: "0 18px 50px rgba(20, 20, 20, 0.06)",
};

const backRow: React.CSSProperties = {
  display: "flex",
  gap: "12px",
  flexWrap: "wrap",
};

const backLink: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  width: "fit-content",
  padding: "12px 18px",
  borderRadius: "999px",
  border: "1px solid var(--line-2)",
  background: "var(--surface-2)",
  color: "var(--text)",
  textDecoration: "none",
  fontWeight: 700,
};

const heroGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1.2fr) minmax(320px, 0.8fr)",
  gap: "20px",
  alignItems: "stretch",
};

const heroLeft: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "18px",
};

const badge: React.CSSProperties = {
  display: "inline-flex",
  width: "fit-content",
  padding: "8px 14px",
  borderRadius: "999px",
  background: "var(--surface-2)",
  border: "1px solid var(--line-2)",
  color: "var(--accent)",
  fontSize: "12px",
  fontWeight: 800,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
};

const title: React.CSSProperties = {
  margin: 0,
  fontSize: "42px",
  lineHeight: 1.05,
  fontWeight: 800,
  letterSpacing: "-0.04em",
  color: "var(--text)",
  fontFamily: "Montserrat",
  textTransform: "uppercase",
};

const subtitle: React.CSSProperties = {
  margin: 0,
  maxWidth: "720px",
  fontSize: "15px",
  lineHeight: 1.7,
  color: "var(--text-muted)",
};

const quickInfoWrap: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: "12px",
};

const quickInfoCard: React.CSSProperties = {
  background: "var(--surface-2)",
  border: "1px solid var(--line)",
  borderRadius: "20px",
  padding: "18px",
};

const quickInfoLabel: React.CSSProperties = {
  margin: "0 0 10px 0",
  fontSize: "12px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--text-dim)",
};

const quickInfoValue: React.CSSProperties = {
  margin: 0,
  fontSize: "28px",
  fontWeight: 800,
  color: "var(--text)",
};

const quickInfoHint: React.CSSProperties = {
  margin: "6px 0 0 0",
  fontSize: "13px",
  color: "var(--text-muted)",
};

const rightAccentCard: React.CSSProperties = {
  height: "100%",
  minHeight: "220px",
  background: "linear-gradient(180deg, #191921 0%, #131318 100%)",
  border: "1px solid var(--line)",
  borderRadius: "24px",
  padding: "22px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
};

const rightAccentTop: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "12px",
};

const rightAccentDot: React.CSSProperties = {
  width: "14px",
  height: "14px",
  borderRadius: "999px",
  background: "var(--accent)",
  boxShadow: "0 0 0 8px rgba(201, 243, 29, 0.14)",
};

const rightAccentTitle: React.CSSProperties = {
  margin: 0,
  fontSize: "15px",
  fontWeight: 800,
  color: "var(--text)",
};

const rightAccentText: React.CSSProperties = {
  margin: "8px 0 0 0",
  fontSize: "14px",
  lineHeight: 1.6,
  color: "var(--text-muted)",
};

const rightAccentBottom: React.CSSProperties = {
  display: "grid",
  gap: "10px",
};

const lineItem: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: "12px",
  paddingBottom: "10px",
  borderBottom: "1px solid var(--line)",
  fontSize: "14px",
};

const metricsGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "16px",
};

const metricCard: React.CSSProperties = {
  background: "var(--surface)",
  borderRadius: "22px",
  padding: "22px",
  border: "1px solid var(--line)",
  boxShadow: "0 10px 25px rgba(20, 20, 20, 0.04)",
};

const metricLabel: React.CSSProperties = {
  margin: "0 0 10px 0",
  fontSize: "12px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--text-dim)",
};

const metricValue: React.CSSProperties = {
  margin: 0,
  fontSize: "34px",
  fontWeight: 800,
  color: "var(--text)",
};

const metricHint: React.CSSProperties = {
  margin: "6px 0 0 0",
  fontSize: "13px",
  color: "var(--text-muted)",
};

const filtersCard: React.CSSProperties = {
  background: "var(--surface-2)",
  border: "1px solid var(--line)",
  borderRadius: "24px",
  padding: "22px",
};

const filtersGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "14px",
};

const fieldWrap: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const fieldLabel: React.CSSProperties = {
  fontSize: "12px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--text-dim)",
};

const fieldControl: React.CSSProperties = {
  width: "100%",
  height: "50px",
  borderRadius: "16px",
  border: "1px solid var(--line)",
  background: "var(--bg-soft)",
  padding: "0 16px",
  fontSize: "14px",
  color: "var(--text)",
  outline: "none",
};

const tableCard: React.CSSProperties = {
  background: "var(--surface)",
  borderRadius: "28px",
  padding: "24px",
  border: "1px solid var(--line)",
  boxShadow: "0 18px 50px rgba(20, 20, 20, 0.06)",
};

const tableHeader: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
  marginBottom: "16px",
};

const tableTitle: React.CSSProperties = {
  margin: 0,
  fontSize: "24px",
  fontWeight: 800,
  color: "var(--text)",
  fontFamily: "Montserrat",
  textTransform: "uppercase",
};

const tableSubtitle: React.CSSProperties = {
  margin: "6px 0 0 0",
  fontSize: "14px",
  color: "var(--text-muted)",
};

const tableWrap: React.CSSProperties = {
  width: "100%",
  overflowX: "auto",
};

const table: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  minWidth: "900px",
};

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "14px 10px",
  borderBottom: "1px solid var(--line)",
  background: "var(--surface-2)",
  color: "var(--text-dim)",
  fontSize: "13px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
};

const td: React.CSSProperties = {
  padding: "14px 10px",
  borderBottom: "1px solid var(--line)",
  color: "var(--text)",
  fontSize: "14px",
};

const evenRowStyle: React.CSSProperties = {
  background: "var(--surface)",
};

const oddRowStyle: React.CSSProperties = {
  background: "var(--surface-2)",
};

const amountPill: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "8px 12px",
  borderRadius: "999px",
  background: "rgba(201, 243, 29, 0.08)",
  border: "1px solid rgba(201, 243, 29, 0.24)",
  color: "var(--accent)",
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const qtyPill: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "8px 12px",
  borderRadius: "999px",
  background: "var(--surface-2)",
  border: "1px solid var(--line)",
  color: "var(--text)",
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const statusDoneStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "8px 12px",
  borderRadius: "999px",
  background: "rgba(111, 227, 111, 0.10)",
  border: "1px solid rgba(111, 227, 111, 0.28)",
  color: "var(--ok)",
  fontWeight: 700,
  whiteSpace: "nowrap",
};

const paginationWrapStyle: React.CSSProperties = {
  marginTop: "18px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
};

const rowsControlStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  color: "var(--text-muted)",
  fontSize: "14px",
  fontWeight: 600,
};

const rowsSelectStyle: React.CSSProperties = {
  height: "40px",
  borderRadius: "12px",
  border: "1px solid var(--line)",
  padding: "0 12px",
  fontSize: "14px",
  color: "var(--text)",
  background: "var(--bg-soft)",
  outline: "none",
};

const pagesWrapStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  flexWrap: "wrap",
};

const pageButtonStyle = (active: boolean): React.CSSProperties => ({
  minWidth: "40px",
  height: "40px",
  padding: "0 12px",
  borderRadius: "12px",
  border: active ? "none" : "1px solid var(--line)",
  background: active ? "var(--accent)" : "var(--surface)",
  color: active ? "var(--on-accent)" : "var(--text-muted)",
  fontWeight: 700,
  fontSize: "14px",
  cursor: "pointer",
  boxShadow: active ? "var(--glow)" : "none",
});

const mutedButtonStyle = (disabled: boolean): React.CSSProperties => ({
  minWidth: "40px",
  height: "40px",
  padding: "0 12px",
  borderRadius: "12px",
  border: "1px solid var(--line)",
  background: "var(--surface)",
  color: disabled ? "var(--text-dim)" : "var(--text-muted)",
  fontWeight: 700,
  fontSize: "14px",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.7 : 1,
});

const emptyState: React.CSSProperties = {
  background: "var(--surface)",
  border: "1px dashed var(--line-2)",
  borderRadius: "18px",
  padding: "24px",
  color: "var(--text-muted)",
  fontSize: "14px",
};

const bottomNote: React.CSSProperties = {
  paddingTop: "8px",
  color: "var(--text-dim)",
  fontSize: "12px",
  lineHeight: 1.5,
};

const PurchaseHistoryPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [period, setPeriod] = useState<HistoryPeriod>("month");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const machine = partners.find((item) => item.id === Number(id));

  const historyData = useMemo<HistoryRow[]>(() => {
  if (!machine) return [];

  const baseLimitByPeriod: Record<HistoryPeriod, number> = {
    today: 8,
    week: 18,
    month: 45,
    "3months": 90,
  };

  const extraRowsByPeriod: Record<HistoryPeriod, number> = {
    today: 0,
    week: 8,
    month: 30,
    "3months": 90,
  };

  const baseLimit = baseLimitByPeriod[period];
  const extraCount = extraRowsByPeriod[period];

  const baseRows: HistoryRow[] = machine.purchases
    .slice(0, baseLimit)
    .map((item, index) => {
      const generatedDate = getHistoryDate(index, period);

      return {
        ...item,
        toy: item.toy,
        time: formatDateTime(generatedDate),
        timestamp: generatedDate.getTime(),
        status: "Выполнено",
        operationType: "Покупка",
      };
    });

  const addRows = (count: number): HistoryRow[] => {
    return Array.from({ length: count }).map((_, index) => {
      const amount = ALLOWED_AMOUNTS[index % ALLOWED_AMOUNTS.length];
      const toy =
        HISTORY_PRODUCT_POOL[(index + machine.id) % HISTORY_PRODUCT_POOL.length];
      const generatedDate = getHistoryDate(index + baseRows.length, period);

      return {
        toy,
        qty: 1,
        amount: formatRub(amount),
        time: formatDateTime(generatedDate),
        timestamp: generatedDate.getTime(),
        status: "Выполнено",
        operationType: "Покупка",
      };
    });
  };

  return [...baseRows, ...addRows(extraCount)].sort(
    (a, b) => b.timestamp - a.timestamp
  );
}, [machine, period]);

  const filteredData = useMemo(() => {
    return historyData.filter((item) =>
      item.toy.toLowerCase().includes(search.toLowerCase())
    );
  }, [historyData, search]);

  const totalRevenue = useMemo(() => {
    return filteredData.reduce((sum, item) => {
      const numeric = Number(item.amount.replace(/[^\d]/g, ""));
      return sum + numeric;
    }, 0);
  }, [filteredData]);

  const totalQty = useMemo(() => {
    return filteredData.reduce((sum, item) => sum + item.qty, 0);
  }, [filteredData]);

  const averageCheck = filteredData.length
    ? Math.round(totalRevenue / filteredData.length)
    : 0;

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));

  useEffect(() => {
    setPage(1);
  }, [period, search, rowsPerPage]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedData = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredData.slice(start, end);
  }, [filteredData, page, rowsPerPage]);

  const visiblePages = useMemo(() => {
    const pages: number[] = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);

    for (let i = start; i <= end; i += 1) {
      pages.push(i);
    }

    return pages;
  }, [page, totalPages]);

  if (!machine) {
    return (
      <div className="page-shell" style={pageWrap}>
        <div className="page-container">
          <TopBar
            title="История покупок"
            subtitle="Просмотр операций по выбранному автомату."
          />

          <div style={container}>
            <div style={backRow}>
              <Link to="/" style={backLink}>
                ← Назад к партнёрам
              </Link>
            </div>

            <div style={sectionCard}>
              <h1 style={{ ...title, fontSize: "34px" }}>История не найдена</h1>
              <p style={subtitle}>
                Проверь ссылку или вернись к списку автоматов и открой историю заново.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell" style={pageWrap}>
      <div className="page-container">
        <TopBar
          title="История покупок"
          subtitle="Полная история операций по выбранному автомату в новом стиле админки."
        />

        <div style={container}>
          <div style={backRow}>
            <Link to="/" style={backLink}>
              ← Назад к партнёрам
            </Link>

            <Link to={`/partner/${machine.id}`} style={backLink}>
              ← Назад к карточке автомата
            </Link>
          </div>

          <div style={sectionCard}>
            <div style={heroGrid}>
              <div style={heroLeft}>
                <div>
                  <div style={badge}>БАТЯ ЗНАЕТ • История</div>
                  <h1 style={title}>
                    {machine.partnerName} · {machine.machineName}
                  </h1>
                  <p style={subtitle}>
                    Здесь отображается история покупок по выбранному автомату.
                    Экран сделан по логике прошлой версии: период, поиск,
                    пагинация и компактная длина страницы.
                  </p>
                </div>

                <div style={quickInfoWrap}>
                  <div style={quickInfoCard}>
                    <p style={quickInfoLabel}>Локация</p>
                    <p style={{ ...quickInfoValue, fontSize: "22px" }}>
                      {machine.location}
                    </p>
                    <p style={quickInfoHint}>точка установки</p>
                  </div>

                  <div style={quickInfoCard}>
                    <p style={quickInfoLabel}>Операций</p>
                    <p style={quickInfoValue}>{filteredData.length}</p>
                    <p style={quickInfoHint}>после фильтрации</p>
                  </div>

                  <div style={quickInfoCard}>
                    <p style={quickInfoLabel}>Период</p>
                    <p style={{ ...quickInfoValue, fontSize: "22px" }}>
                      {period === "today"
                        ? "За 1 день"
                        : period === "week"
                        ? "Неделя"
                        : period === "month"
                        ? "Месяц"
                        : "3 месяца"}
                    </p>
                    <p style={quickInfoHint}>активный фильтр</p>
                  </div>
                </div>
              </div>

              <div style={rightAccentCard}>
                <div style={rightAccentTop}>
                  <div>
                    <p style={rightAccentTitle}>Информация</p>
                    <p style={rightAccentText}>
                      Выбор партнёра происходит на этой странице. Переход дальше
                      выполняется уже из карточки конкретного автомата.
                    </p>
                  </div>

                  <div style={rightAccentDot} />
                </div>

                <div style={rightAccentBottom}>
                  <div style={lineItem}>
                    <span>Партнёр</span>
                    <strong>{machine.partnerName}</strong>
                  </div>
                  <div style={lineItem}>
                    <span>ID автомата</span>
                    <strong>#{machine.id}</strong>
                  </div>
                  <div
                    style={{ ...lineItem, borderBottom: "none", paddingBottom: 0 }}
                  >
                    <span>Версия</span>
                    <strong>{machine.version}</strong>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={metricsGrid}>
            <div style={metricCard}>
              <p style={metricLabel}>Выручка по фильтру</p>
              <p style={metricValue}>{formatRub(totalRevenue)}</p>
              <p style={metricHint}>сумма всех строк после фильтра</p>
            </div>

            <div style={metricCard}>
              <p style={metricLabel}>Количество товаров</p>
              <p style={metricValue}>{totalQty}</p>
              <p style={metricHint}>сумма quantity по строкам</p>
            </div>

            <div style={metricCard}>
              <p style={metricLabel}>Средний чек</p>
              <p style={metricValue}>{formatRub(averageCheck)}</p>
              <p style={metricHint}>по текущей выборке</p>
            </div>

            <div style={metricCard}>
              <p style={metricLabel}>Страниц</p>
              <p style={metricValue}>{totalPages}</p>
              <p style={metricHint}>при {rowsPerPage} строках на странице</p>
            </div>
          </div>

          <div style={filtersCard}>
            <div style={filtersGrid}>
              <div style={fieldWrap}>
                <span style={fieldLabel}>Период</span>
                <select
                  style={fieldControl}
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as HistoryPeriod)}
                >
                  <option value="today">За 1 день</option>
                  <option value="week">Неделя</option>
                  <option value="month">Месяц</option>
                  <option value="3months">3 месяца</option>
                </select>
              </div>

              <div style={fieldWrap}>
                <span style={fieldLabel}>Поиск по товару</span>
                <input
                  style={fieldControl}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Например: лампа, омывайка, зарядка"
                />
              </div>

              <div style={fieldWrap}>
                <span style={fieldLabel}>Автомат</span>
                <input style={fieldControl} value={machine.machineName} readOnly />
              </div>

              <div style={fieldWrap}>
                <span style={fieldLabel}>Локация</span>
                <input style={fieldControl} value={machine.location} readOnly />
              </div>
            </div>
          </div>

          <div style={tableCard}>
            <div style={tableHeader}>
              <div>
                <h2 style={tableTitle}>Операции по автомату</h2>
                <p style={tableSubtitle}>
                  История разбита по страницам, поэтому экран не становится бесконечно длинным.
                </p>
              </div>
            </div>

            {filteredData.length === 0 ? (
              <div style={emptyState}>По текущим фильтрам операций не найдено.</div>
            ) : (
              <>
                <div style={tableWrap}>
                  <table style={table}>
                    <thead>
                      <tr>
                        <th style={th}>Тип операции</th>
                        <th style={th}>Товар</th>
                        <th style={th}>Количество</th>
                        <th style={th}>Статус</th>
                        <th style={th}>Сумма</th>
                        <th style={th}>Дата / время</th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginatedData.map((item, index) => {
                        const rowBase =
                          index % 2 === 0 ? evenRowStyle : oddRowStyle;

                        return (
                          <tr
                            key={`${item.time}-${item.toy}-${index}`}
                            style={rowBase}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background =
                                "rgba(201, 243, 29, 0.04)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background =
                                rowBase.background as string;
                            }}
                          >
                            <td style={td}>{item.operationType}</td>
                            <td style={td}>{item.toy}</td>
                            <td style={td}>
                              <span style={qtyPill}>{item.qty}</span>
                            </td>
                            <td style={td}>
                              <span style={statusDoneStyle}>{item.status}</span>
                            </td>
                            <td style={td}>
                              <span style={amountPill}>{item.amount}</span>
                            </td>
                            <td style={td}>{item.time}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div style={paginationWrapStyle}>
                  <div style={rowsControlStyle}>
                    <span>Строк на странице:</span>

                    <select
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(Number(e.target.value));
                        setPage(1);
                      }}
                      style={rowsSelectStyle}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </div>

                  <div style={pagesWrapStyle}>
                    <button
                      type="button"
                      style={mutedButtonStyle(page === 1)}
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      disabled={page === 1}
                    >
                      ←
                    </button>

                    {visiblePages.map((pageNumber) => (
                      <button
                        key={pageNumber}
                        type="button"
                        style={pageButtonStyle(page === pageNumber)}
                        onClick={() => setPage(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    ))}

                    <button
                      type="button"
                      style={mutedButtonStyle(page === totalPages)}
                      onClick={() =>
                        setPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      disabled={page === totalPages}
                    >
                      →
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div style={bottomNote}>
            Все имена и события вымышлены, любые совпадения с реальными людьми и событиями случайны.
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseHistoryPage;
